import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fmtCurrency, fmtUnitRate } from '../lib/display-labels';
import type { QuotePricingItemsResponse, QuotePricingSourcesResponse } from '../types/quote-pricing';
import { UNIT_OPTIONS } from '../constants/units';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Search,
  Building2,
  FileText,
  ChevronRight,
  CheckSquare,
  Square,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { api, getApiErrorMessage } from '../api/client';
import { toast } from 'sonner';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { formatCalendarDate } from '../lib/calendar-date';
import { PF_QUOTE_VERTICAL, pfQuoteItemTerms, pfQuoteRefusal } from '../lib/pf-quotes';
import { WT_QUOTE_VERTICAL, wtQuoteItemTerms, wtQuoteRefusal } from '../lib/wt-quotes';
import { EW_QUOTE_VERTICAL, ewQuoteItemTerms, ewQuoteRefusal } from '../lib/ew-quotes';
import { WS_QUOTE_VERTICAL, wsQuoteItemTerms, wsQuoteRefusal } from '../lib/ws-quotes';
import { WL_QUOTE_VERTICAL, wlQuoteItemTerms, wlQuoteRefusal } from '../lib/wireline-slickline-quotes';

interface Customer {
  id: string;
  name: string;
  mainContactName?: string;
  main_contact_name?: string;
  city?: string;
  provinceState?: string;
  province_state?: string;
}

/**
 * The pricebook tab reads GET /quotes/pricing-sources/casing_running/items: only
 * sheets that may price THIS customer's quote today — active, a company book or
 * the customer's own, in force — the same rule POST /from-rate-sheet enforces.
 * Picking from rateSheets.list() offered expired sheets and other customers'
 * bids, which the create then refused.
 */

interface ManualLineItem {
  lineItemType: string;
  description: string;
  unit: string;
  rate: number;
  quantity: number;
}

const STEP_LABELS = ['Select Customer', 'Add Line Items', 'Options', 'Review'];
const TYPE_OPTIONS = ['service', 'equipment', 'labour', 'materials', 'other'];

/**
 * @see fmtCurrency — it owns the '$', which is why the call sites below no
 * longer prefix one. A caller-supplied '$' would render an absent amount as
 * '$—'.
 */
const fmt = (n: number | string | null | undefined) => fmtCurrency(n);

export default function QuoteCreateScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // "New quote" from an opportunity (CRM-FE-3): ?customer=&opportunity=. The
  // customer starts chosen; once the quote exists it is LINKED to the
  // opportunity through the link route, which works for every create path
  // (manual, rate sheet, price book) and refuses a different customer.
  const [searchParams] = useSearchParams();
  const opportunityParam = searchParams.get('opportunity');
  const customerParam = searchParams.get('customer');
  const [step, setStep] = useState(0);
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerParam ?? '');
  const [searchQ, setSearchQ] = useState('');
  const [tab, setTab] = useState<'manual' | 'pricebook'>('manual');

  // Which vertical's rates price this quote (api API-1). One vertical per quote.
  const [chosenVertical, setChosenVertical] = useState<string | null>(null);

  // Rate sheet
  const [selectedRateSheetId, setSelectedRateSheetId] = useState('');
  // Non-casing pricebooks have no sheet to pick: every applicable item is
  // listed, and each picked item carries its own quantity (text, as typed).
  const [qtyById, setQtyById] = useState<Record<string, string>>({});
  // One item is one rate of one row: (sourceId, basis). Downhole rows carry
  // several rates (api API-3); single-price stores have no basis.
  const itemKey = (it: { sourceId: string; basis?: string | null }) => `${it.sourceId}|${it.basis ?? ''}`;
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  // Manual items
  const [manualItems, setManualItems] = useState<ManualLineItem[]>([]);
  const [newItem, setNewItem] = useState<ManualLineItem>({ lineItemType: 'service', description: '', unit: 'per_hour', rate: 0, quantity: 1 });

  // Options
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');

  // Queries
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const res = await api.customers.list();
      return unwrapArray<any>(res.data);
    },
  });

  const { data: sourcesData } = useQuery({
    queryKey: ['quote-pricing-sources'],
    queryFn: async () => {
      const res = await api.quotes.pricingSources();
      return unwrap<QuotePricingSourcesResponse['data']>(res.data) ?? [];
    },
  });
  const readySources = useMemo(() => (sourcesData ?? []).filter((s) => s.ready), [sourcesData]);
  // Casing first when the tenant has it (every quote before API-1 was casing),
  // else the first vertical whose quoting has shipped.
  const vertical = chosenVertical
    ?? readySources.find((s) => s.vertical === 'casing_running')?.vertical
    ?? readySources[0]?.vertical
    ?? 'casing_running';
  const isCasing = vertical === 'casing_running';
  const storeLabel = readySources.find((s) => s.vertical === vertical)?.storeLabel ?? 'Pricebook';

  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: ['quote-pricing-items', vertical, selectedCustomerId],
    queryFn: async () => {
      const res = await api.quotes.pricingItems(vertical, selectedCustomerId);
      return unwrap<QuotePricingItemsResponse['data']>(res.data)?.items ?? [];
    },
    enabled: step === 1 && tab === 'pricebook' && !!selectedCustomerId,
  });

  const customers = (customersData || []) as Customer[];
  const pricingItems = useMemo(() => pricingData ?? [], [pricingData]);
  // The sheets that apply, in the API's order (the customer's own first).
  const rateSheets = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; own: boolean }>();
    for (const it of pricingItems) {
      if (it.group && !seen.has(it.group.id)) seen.set(it.group.id, { ...it.group, own: it.priceScope === 'customer' });
    }
    return Array.from(seen.values());
  }, [pricingItems]);
  const rateSheetItems = pricingItems.filter((it) => it.group?.id === selectedRateSheetId);
  // A picked non-casing item with a positive quantity; anything else is not a line.
  const pickedLines = useMemo(() => pricingItems
    .filter((it) => qtyById[itemKey(it)] !== undefined)
    .map((it) => ({ item: it, quantity: Number(qtyById[itemKey(it)]) })),
  [pricingItems, qtyById]);
  const pickedValid = pickedLines.length > 0 && pickedLines.every((l) => Number.isFinite(l.quantity) && l.quantity > 0);

  const chooseVertical = (v: string) => {
    setChosenVertical(v);
    setSelectedRateSheetId('');
    setSelectedItemIds([]);
    setQtyById({});
  };

  const filteredCustomers = useMemo(() => {
    if (!searchQ) return customers;
    const q = searchQ.toLowerCase();
    return customers.filter(c => c.name.toLowerCase().includes(q));
  }, [customers, searchQ]);

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  const afterCreate = async (quoteId: string | undefined) => {
    if (quoteId && opportunityParam && selectedCustomerId === customerParam) {
      try {
        const res = await api.opportunities.linkQuote(opportunityParam, quoteId);
        queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityParam] });
        // D16: the API may SUGGEST 'quoted'; the opportunity page offers it.
        navigate(`/sales/pipeline/${opportunityParam}`, {
          replace: true,
          state: { suggestedStage: res.data?.data?.suggestedStage ?? null },
        });
        return;
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'The quote was created but could not be linked to the opportunity.'));
      }
    }
    navigate(quoteId ? `/quotes/${quoteId}` : '/quotes', { replace: true });
  };

  // Create mutations
  const createManualMutation = useMutation({
    mutationFn: (data: any) => api.quotes.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote-dashboard'] });
      afterCreate(res.data?.data?.id);
    },
  });

  const createFromRateSheetMutation = useMutation({
    mutationFn: (data: any) => api.quotes.createFromRateSheet(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote-dashboard'] });
      afterCreate(res.data?.data?.id);
    },
  });

  const createFromSourceMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.quotes.createFromPricingSource>[0]) => api.quotes.createFromPricingSource(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote-dashboard'] });
      afterCreate(res.data?.data?.id);
    },
  });

  const isCreating = createManualMutation.isPending || createFromRateSheetMutation.isPending || createFromSourceMutation.isPending;
  const createError = createManualMutation.error || createFromRateSheetMutation.error || createFromSourceMutation.error;

  const toggleItemId = (id: string) => {
    setSelectedItemIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const addManualItem = () => {
    if (!newItem.description || newItem.rate <= 0) return;
    setManualItems(prev => [...prev, { ...newItem }]);
    setNewItem({ lineItemType: 'service', description: '', unit: 'per_hour', rate: 0, quantity: 1 });
  };

  const removeManualItem = (index: number) => {
    setManualItems(prev => prev.filter((_, i) => i !== index));
  };

  const computedTotal = useMemo(() => {
    if (tab === 'manual') {
      return manualItems.reduce((sum, li) => sum + li.rate * li.quantity, 0);
    }
    if (!isCasing) {
      return pickedLines.reduce((sum, l) => sum + Number(l.item.rate ?? 0) * (Number.isFinite(l.quantity) ? l.quantity : 0), 0);
    }
    return rateSheetItems
      .filter((item) => selectedItemIds.includes(item.sourceId))
      .reduce((sum: number, item) => sum + Number(item.rate ?? 0), 0);
  }, [tab, manualItems, rateSheetItems, selectedItemIds, isCasing, pickedLines]);

  const canProceed = () => {
    if (step === 0) return !!selectedCustomerId;
    if (step === 1) return tab === 'manual' ? manualItems.length > 0 : isCasing ? selectedItemIds.length > 0 : pickedValid;
    return true;
  };

  const handleCreate = () => {
    if (tab === 'pricebook' && !isCasing) {
      createFromSourceMutation.mutate({
        vertical,
        customerId: selectedCustomerId,
        lines: pickedLines.map((l) => ({
          sourceId: l.item.sourceId,
          ...(l.item.basis ? { basis: l.item.basis } : {}),
          quantity: l.quantity,
        })),
        validUntil: validUntil || undefined,
        notes: notes || undefined,
      });
    } else if (tab === 'pricebook' && selectedRateSheetId && selectedItemIds.length > 0) {
      createFromRateSheetMutation.mutate({
        customerId: selectedCustomerId,
        rateSheetId: selectedRateSheetId,
        itemIds: selectedItemIds,
        validUntil: validUntil || undefined,
        notes: notes || undefined,
      });
    } else {
      createManualMutation.mutate({
        customerId: selectedCustomerId,
        // Absent means casing on the API; only a non-casing quote names it.
        ...(isCasing ? {} : { vertical }),
        lineItems: manualItems,
        validUntil: validUntil || undefined,
        notes: notes || undefined,
      });
    }
  };

  return (
    <div className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => step > 0 ? setStep(step - 1) : navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">New Quote</h1>
            <p className="text-xs text-fieldiq-text">{STEP_LABELS[step]}</p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="max-w-[1920px] mx-auto px-4 py-3">
        <div className="flex gap-2">
          {STEP_LABELS.map((label, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`} />
              <p className={`text-xs mt-1 ${i === step ? 'text-fieldiq-accent font-medium' : 'text-fieldiq-muted'}`}>
                {label}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-2">
        {/* Step 0: Customer Selection */}
        {step === 0 && (
          <div className="space-y-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fieldiq-muted" />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent"
              />
            </div>

            <div className="space-y-2">
              {filteredCustomers.map((c) => (
                <div
                  key={c.id}
                  onClick={() => setSelectedCustomerId(c.id)}
                  className={`bg-fieldiq-card rounded-xl p-4 flex items-center gap-3 cursor-pointer transition-colors border ${
                    selectedCustomerId === c.id ? 'border-fieldiq-accent' : 'border-fieldiq-border hover:border-fieldiq-muted'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg bg-fieldiq-dark flex items-center justify-center">
                    <Building2 size={18} className="text-fieldiq-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate">{c.name}</h3>
                    <p className="text-xs text-fieldiq-text">
                      {c.mainContactName || c.main_contact_name || ''} {c.city || ''}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-fieldiq-muted" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Line Items */}
        {step === 1 && (
          <div className="space-y-4">
            {readySources.length > 1 && (
              <div data-testid="quote-vertical-picker">
                <p className="text-xs text-fieldiq-text mb-1">Price this quote from</p>
                <div className="flex gap-2">
                  {readySources.map((src) => (
                    <button
                      key={src.vertical}
                      onClick={() => chooseVertical(src.vertical)}
                      aria-pressed={src.vertical === vertical}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium ${src.vertical === vertical ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-text'}`}
                    >
                      {src.label} &middot; {src.storeLabel}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setTab('manual')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${tab === 'manual' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-text'}`}
              >
                Manual
              </button>
              <button
                onClick={() => setTab('pricebook')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${tab === 'pricebook' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-text'}`}
              >
                {isCasing ? 'From Pricebook' : `From ${storeLabel}`}
              </button>
            </div>

            {tab === 'manual' && (
              <div className="space-y-3">
                {manualItems.map((item, idx) => (
                  <div key={idx} className="bg-fieldiq-card rounded-xl p-3 border border-fieldiq-border flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">{item.description}</p>
                      <p className="text-xs text-fieldiq-text">{item.quantity} x {fmt(item.rate)} = {fmt(item.quantity * item.rate)}</p>
                    </div>
                    <button onClick={() => removeManualItem(idx)} className="text-red-400 hover:text-red-300">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-3">
                  <h3 className="text-sm font-semibold text-white">Add Line Item</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={newItem.lineItemType}
                      onChange={(e) => setNewItem({ ...newItem, lineItemType: e.target.value })}
                      className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                      value={newItem.unit}
                      onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                      className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </div>
                  <input
                    placeholder="Description"
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Rate"
                      value={newItem.rate || ''}
                      onChange={(e) => setNewItem({ ...newItem, rate: Number(e.target.value) })}
                      className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={newItem.quantity || ''}
                      onChange={(e) => setNewItem({ ...newItem, quantity: Number(e.target.value) })}
                      className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                    />
                  </div>
                  <button
                    onClick={addManualItem}
                    disabled={!newItem.description || newItem.rate <= 0}
                    className="w-full flex items-center justify-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold py-2 rounded-lg disabled:opacity-50"
                  >
                    <Plus size={16} />
                    Add Item
                  </button>
                </div>
              </div>
            )}

            {tab === 'pricebook' && !isCasing && (
              <div className="space-y-3" data-testid="source-items">
                {!pricingLoading && pricingItems.length === 0 && (
                  <p className="text-sm text-fieldiq-text" data-testid="no-applicable-items">
                    Nothing in {storeLabel} applies to this customer today.
                  </p>
                )}
                {pricingItems.map((item, index) => {
                  const key = itemKey(item);
                  // DD/MWD crew rates (api#1092) follow the tools, under their own
                  // heading: they bind customer crew rates on accept, not tool rates.
                  const firstCrew = item.sourceKind === 'dh_personnel_rate'
                    && index === pricingItems.findIndex((it) => it.sourceKind === 'dh_personnel_rate');
                  // An item the store lists but will not let be quoted (api#1232:
                  // a Pipeline rate tied with another). Shown with the API's reason,
                  // never pickable. Absent on every other source, so they are unchanged.
                  const refused = item.refusal ?? null;
                  const picked = !refused && qtyById[key] !== undefined;
                  const t = item.terms;
                  const terms = [
                    t?.rateType ? String(t.rateType) : null,
                    t?.minimumDays ? `min ${t.minimumDays} day${Number(t.minimumDays) === 1 ? '' : 's'}` : null,
                    t?.billingTrigger ? `bills from ${String(t.billingTrigger).replace(/_/g, ' ')}` : null,
                    t?.standbyRate ? `standby ${fmtUnitRate(t.standbyRate as string)}` : null,
                    // Downhole: shown, never priced — whether they apply is decided by the run.
                    t?.minimumCharge ? `minimum ${fmtUnitRate(t.minimumCharge as string)}` : null,
                    t?.lihRate ? `lost in hole ${fmtUnitRate(t.lihRate as string)}` : null,
                  ].filter(Boolean).join(' · ');
                  // Pipeline Rate Card: code · kind · what it applies to · notes. Null elsewhere.
                  const pfTerms = pfQuoteItemTerms(item);
                  // Well Testing Rate Card, the same four terms. Null elsewhere.
                  const wtTerms = wtQuoteItemTerms(item);
                  // Earthworks Rate Card: code · kind · how it prices · notes. Null elsewhere.
                  const ewTerms = ewQuoteItemTerms(item);
                  // Well Servicing Rate Card: code · kind · what it applies to · notes. Null elsewhere.
                  const wsTerms = wsQuoteItemTerms(item);
                  // Wireline & Slickline Rate Card: code · kind · basis · what it applies to · notes. Null elsewhere.
                  const wlTerms = wlQuoteItemTerms(item);
                  return (
                    <React.Fragment key={key}>
                    {firstCrew && (
                      <h3 className="pt-2 text-xs font-black uppercase tracking-[0.12em] text-fieldiq-muted" data-testid="crew-rates-heading">
                        Crew rates (Directional &amp; MWD)
                      </h3>
                    )}
                    <div
                      data-testid={refused ? 'source-item-refused' : undefined}
                      className={`bg-fieldiq-card rounded-xl p-3 flex items-center gap-3 border ${picked ? 'border-fieldiq-accent' : 'border-fieldiq-border'} ${refused ? 'opacity-70' : ''}`}
                    >
                      <button
                        type="button"
                        aria-label={`${picked ? 'Remove' : 'Add'} ${item.label}`}
                        disabled={!!refused}
                        className="disabled:cursor-not-allowed"
                        onClick={() => refused ? undefined : setQtyById((prev) => {
                          const next = { ...prev };
                          if (picked) delete next[key]; else next[key] = '1';
                          return next;
                        })}
                      >
                        {picked ? <CheckSquare size={18} className="text-fieldiq-accent" /> : <Square size={18} className="text-fieldiq-muted" />}
                      </button>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">
                          {item.label}{item.priceScope === 'customer' ? ' (this customer)' : ''}
                        </p>
                        <p className="text-xs text-fieldiq-text">{[item.description, terms, pfTerms, wtTerms, ewTerms, wsTerms, wlTerms].filter(Boolean).join(' · ')}</p>
                        {refused && (
                          <p className="mt-1 text-xs font-semibold text-fieldiq-text" data-testid="source-item-refusal">
                            Cannot be quoted: {refused.error}
                          </p>
                        )}
                      </div>
                      {picked && (
                        <input
                          type="number"
                          min="0"
                          step="any"
                          aria-label={`Quantity for ${item.label}`}
                          value={qtyById[key]}
                          onChange={(e) => setQtyById((prev) => ({ ...prev, [key]: e.target.value }))}
                          className="w-20 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1 text-sm text-white text-right"
                        />
                      )}
                      <span className="text-sm font-semibold text-fieldiq-accent">{fmtUnitRate(item.rate)}</span>
                    </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {tab === 'pricebook' && isCasing && (
              <div className="space-y-3">
                <select
                  value={selectedRateSheetId}
                  onChange={(e) => { setSelectedRateSheetId(e.target.value); setSelectedItemIds([]); }}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white"
                >
                  <option value="">Select Rate Sheet...</option>
                  {rateSheets.map((rs) => (
                    <option key={rs.id} value={rs.id}>{rs.own ? `${rs.name} (this customer)` : rs.name}</option>
                  ))}
                </select>

                {!pricingLoading && rateSheets.length === 0 && (
                  <p className="text-sm text-fieldiq-text" data-testid="no-applicable-sheets">
                    No rate sheet applies to this customer today. A sheet must be active, a company book or this
                    customer&apos;s own, and in force today.
                  </p>
                )}

                {rateSheetItems.map((item) => {
                  const isSelected = selectedItemIds.includes(item.sourceId);
                  return (
                    <div
                      key={item.sourceId}
                      onClick={() => toggleItemId(item.sourceId)}
                      className={`bg-fieldiq-card rounded-xl p-3 flex items-center gap-3 cursor-pointer border transition-colors ${
                        isSelected ? 'border-fieldiq-accent' : 'border-fieldiq-border hover:border-fieldiq-muted'
                      }`}
                    >
                      {isSelected ? <CheckSquare size={18} className="text-fieldiq-accent" /> : <Square size={18} className="text-fieldiq-muted" />}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{item.label}</p>
                        <p className="text-xs text-fieldiq-text">{(item.lineItemType || '').replace(/_/g, ' ')} &middot; {item.unit?.replace(/_/g, ' ')}</p>
                      </div>
                      <span className="text-sm font-semibold text-fieldiq-accent">{fmtUnitRate(item.rate)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {(manualItems.length > 0 || selectedItemIds.length > 0 || pickedLines.length > 0) && (
              <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
                <div className="flex justify-between text-sm">
                  <span className="text-fieldiq-text">Estimated Total</span>
                  <span className="text-white font-bold">{fmt(computedTotal)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Options */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-4">
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Valid Until</label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent"
                />
              </div>
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Terms, conditions, or special notes..."
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent resize-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
              <h3 className="text-sm font-semibold text-fieldiq-text uppercase mb-2">Customer</h3>
              <p className="text-white font-medium">{selectedCustomer?.name || '—'}</p>
            </div>

            <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-2">
              <h3 className="text-sm font-semibold text-fieldiq-text uppercase mb-2">Line Items</h3>
              {tab === 'manual' ? (
                manualItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-white">{item.description}</span>
                    <span className="text-fieldiq-accent font-medium">{fmt(item.rate * item.quantity)}</span>
                  </div>
                ))
              ) : !isCasing ? (
                pickedLines.map(({ item, quantity }) => (
                  <div key={itemKey(item)} className="flex justify-between text-sm">
                    <span className="text-white">{item.label} &times; {quantity}</span>
                    <span className="text-fieldiq-accent font-medium">{fmt(Number(item.rate ?? 0) * quantity)}</span>
                  </div>
                ))
              ) : (
                rateSheetItems
                  .filter((item) => selectedItemIds.includes(item.sourceId))
                  .map((item) => (
                    <div key={item.sourceId} className="flex justify-between text-sm">
                      <span className="text-white">{item.label}</span>
                      <span className="text-fieldiq-accent font-medium">{fmtUnitRate(item.rate)}</span>
                    </div>
                  ))
              )}
              <div className="border-t border-fieldiq-border pt-2 mt-2 flex justify-between text-base font-bold">
                <span className="text-white">Estimated Total</span>
                <span className="text-fieldiq-accent">{fmt(computedTotal)}</span>
              </div>
            </div>

            {(validUntil || notes) && (
              <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-2">
                {validUntil && (
                  <div className="flex justify-between text-sm">
                    <span className="text-fieldiq-text">Valid Until</span>
                    {/* `validUntil` is the raw value of the <input type="date"> above — a bare
                          'YYYY-MM-DD'. `new Date()` on it is UTC midnight, so the review step
                          confirmed the day BEFORE the one the user had just picked. The quote
                          itself was always right; only this summary lied. */}
                      <span className="text-white">{formatCalendarDate(validUntil) ?? '—'}</span>
                  </div>
                )}
                {notes && <p className="text-sm text-fieldiq-text">{notes}</p>}
              </div>
            )}

            {createError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
                {/* A Pipeline quote's refusal as a sentence (lib/pf-quotes); every other vertical as before. */}
                {vertical === PF_QUOTE_VERTICAL && createError === createFromSourceMutation.error
                  ? pfQuoteRefusal(createError, 'Failed to create quote').error
                  : vertical === WT_QUOTE_VERTICAL && createError === createFromSourceMutation.error
                  ? wtQuoteRefusal(createError, 'Failed to create quote').error
                  : vertical === EW_QUOTE_VERTICAL && createError === createFromSourceMutation.error
                  ? ewQuoteRefusal(createError, 'Failed to create quote').error
                  : vertical === WS_QUOTE_VERTICAL && createError === createFromSourceMutation.error
                  ? wsQuoteRefusal(createError, 'Failed to create quote').error
                  : vertical === WL_QUOTE_VERTICAL && createError === createFromSourceMutation.error
                  ? wlQuoteRefusal(createError, 'Failed to create quote').error
                  : (createError as any)?.response?.data?.error || 'Failed to create quote'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Action */}
      <div className="border-t border-fieldiq-border px-4 py-3 mt-4">
        <div className="max-w-[1920px] mx-auto">
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3 rounded-xl disabled:opacity-50"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isCreating && <Loader2 size={18} className="animate-spin" />}
              Create Quote
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
